<template>
  <div class="promo-wrapper" :class="{ 'scrolled': isScrolled }">
    <UkaskoLoader :is-loading="!isPromoTextBlocksReady">
      <PromoHeroSection :animation-enabled="isAnimationsEnabled" :mobile-menu-open="isMobileMenuOpen"/>
      <PromoConnectUkaskoSection :animation-enabled="isAnimationsEnabled" />
      <PromoFAQSection :animation-enabled="isAnimationsEnabled" />
      <PromoPartnersInformationSection :animation-enabled="isAnimationsEnabled" />
      <PromoContactsAndSubscribeSection :animation-enabled="isAnimationsEnabled" />
      <AppFooter />
      <GrayFancyButton
        v-show="showDisableAnimationButton && isAnimationsEnabled"
        class="disable-animations-button"
        @click="disableAnimations"
      >
        Вимкнути анімацію
      </GrayFancyButton>
      <GrayFancyButton
        v-show="showDisableAnimationButton && !isAnimationsEnabled"
        class="enable-animations-button"
        @click="enableAnimations"
      >
        Увімкнути анімацію
      </GrayFancyButton>
      <ScrollToTop
        class="scroll-to-top"
        :style="scrollToTopStyle"
        v-show="!isMobileMenuOpen"
      />
    </UkaskoLoader>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PromoConnectUkaskoSection from '../connect-ukasko';
import PromoContactsAndSubscribeSection from '../contacts-and-subscribe';
import PromoFAQSection from '../faq';
import PromoHeroSection from '../hero';
import PromoPartnersInformationSection from '../partners-information';
import { trackScroll } from '@/shared/lib/scroll';
import { GrayFancyButton } from '@/shared/ui/form';
import { UkaskoLoader } from '@/shared/ui/ukasko-loader';
import { AppFooter, PROMO_MOBILE_MENU_EVENTS } from '@/widgets/promo';
import { ScrollToTop } from '@/widgets/scroll-to-top';

const HIDE_DISABLE_ANIMATION_BUTTON_POSITION = 500;
const FLOATING_WIDGET_SELECTORS = ['#bwc-widget-action', '#bwc-chat-omnichannel'];
const FLOATING_WIDGET_GAP = 16;

export default {
  data() {
    return {
      showDisableAnimationButton: true,
      isMobileMenuOpen: false,
      isScrolled: false,
      scrollToTopStyle: null,
      widgetsObserver: null,
      widgetsFrame: null,
    };
  },
  mixins: [ trackScroll ],
  created() {
    this.$eventBus.$on(PROMO_MOBILE_MENU_EVENTS.PROMO_MOBILE_MENU_OPEN, this.openMobileMenu);
    this.$eventBus.$on(PROMO_MOBILE_MENU_EVENTS.PROMO_MOBILE_MENU_CLOSE, this.closeMobileMenu);
  },
  mounted() {
    this.fetchTextBlocks('promo').then(() => {
      if (this.$route.hash) {
        this.scrollToHash(this.$route.hash);
      }
    });
    this.subscribeToScroll((position) => {
      this.showDisableAnimationButton = position.bottom > HIDE_DISABLE_ANIMATION_BUTTON_POSITION;
      this.isScrolled = window.scrollY > 0;
    });

    const currentRoute = this.$router?.history?.current || null;
    const lastRoute = this.$router?.app?.$lastRoute || null;

    if (!currentRoute?.hash && (lastRoute?.name === 'insurance-companies' || lastRoute?.name === 'insurance-intermediary')) {
      setTimeout(() => {
        this.scrollToHash('#partners-information');
      }, 500);
    }

    this.widgetsObserver = new MutationObserver(this.scheduleScrollToTopPosition);
    this.widgetsObserver.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', this.scheduleScrollToTopPosition);
    document.addEventListener('animationend', this.scheduleScrollToTopPosition, true);
    document.addEventListener('transitionend', this.scheduleScrollToTopPosition, true);
    this.updateScrollToTopPosition();
  },
  beforeDestroy() {
    this.widgetsObserver.disconnect();
    window.removeEventListener('resize', this.scheduleScrollToTopPosition);
    document.removeEventListener('animationend', this.scheduleScrollToTopPosition, true);
    document.removeEventListener('transitionend', this.scheduleScrollToTopPosition, true);
    cancelAnimationFrame(this.widgetsFrame);
    this.$eventBus.$off(PROMO_MOBILE_MENU_EVENTS.PROMO_MOBILE_MENU_OPEN, this.openMobileMenu);
    this.$eventBus.$off(PROMO_MOBILE_MENU_EVENTS.PROMO_MOBILE_MENU_CLOSE, this.closeMobileMenu);
  },
  components: {
    PromoHeroSection, PromoConnectUkaskoSection, PromoFAQSection, PromoPartnersInformationSection, AppFooter,
    PromoContactsAndSubscribeSection, GrayFancyButton, UkaskoLoader, ScrollToTop
  },
  computed: {
    ...mapGetters('promoAnimationsModule', ['isAnimationsEnabled']),
    ...mapGetters('textBlocksModule', ['isTextBlocksReady']),
    isPromoTextBlocksReady() {
      return this.isTextBlocksReady('promo');
    },
  },
  methods: {
    ...mapActions('promoAnimationsModule', ['enableAnimations', 'disableAnimations']),
    ...mapActions('textBlocksModule', ['fetchTextBlocks']),
    toggleMobileMenu(isMenuOpen) {
      this.showDisableAnimationButton = !isMenuOpen;
      this.isMobileMenuOpen = isMenuOpen;
    },
    openMobileMenu() {
      this.toggleMobileMenu(true);
    },
    closeMobileMenu() {
      this.toggleMobileMenu(false);
    },
    scheduleScrollToTopPosition() {
      if (this.widgetsFrame) {
        return;
      }

      this.widgetsFrame = requestAnimationFrame(() => {
        this.widgetsFrame = null;
        this.updateScrollToTopPosition();
      });
    },
    updateScrollToTopPosition() {
      const rects = FLOATING_WIDGET_SELECTORS
        .map((selector) => document.querySelector(selector))
        .filter((element) => element && window.getComputedStyle(element).visibility !== 'hidden')
        .map((element) => element.getBoundingClientRect())
        .filter((rect) => rect.width > 0 && rect.height > 0);

      if (!rects.length) {
        this.scrollToTopStyle = null;
        return;
      }

      const top = Math.min(...rects.map((rect) => rect.top));
      const right = Math.max(...rects.map((rect) => rect.right));
      const style = {
        bottom: `${Math.round(window.innerHeight - top + FLOATING_WIDGET_GAP)}px`,
        right: `${Math.round(document.documentElement.clientWidth - right)}px`,
      };

      if (this.scrollToTopStyle?.bottom !== style.bottom || this.scrollToTopStyle?.right !== style.right) {
        this.scrollToTopStyle = style;
      }
    },
    scrollToHash(hash) {
      const retries = 5;
      const delay = 100;
      const tryScroll = (attempt) => {
        const targetElement = document.querySelector(hash);
        if (targetElement) {
          window.scrollTo({
            top: targetElement.offsetTop,
            behavior: 'smooth',
          });
        } else if (attempt < retries) {
          setTimeout(() => tryScroll(attempt + 1), delay);
        }
      };
      tryScroll(0);
    },
  },
  watch: {
    '$route.hash': {
      handler(newHash) {
        if (newHash && this.isTextBlocksReady('promo')) {
          this.scrollToHash(newHash);
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.disable-animations-button, .enable-animations-button {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 11;

  @media (max-width: 900px) {
    left: 30%;
  }

  @media (max-width: 750px) {
    left: 25%;
  }

  @media (max-width: 650px) {
    left: 50%;
  }
}

.scroll-to-top {
  position: fixed;
  bottom: 100px;
  right: 30px;
  z-index: 100;
}

.scrolled {
  ::v-deep .app-header-desktop, ::v-deep .app-header-mobile {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
  }

  ::v-deep .app-header-desktop {
    height: 100px !important;
    padding: 0 56px 16px !important;
    background: linear-gradient(to bottom, #CADDFC 0px, #CADDFC 80px, rgba(202, 221, 252, 0) 100px) !important;
    z-index: 100 !important;
    transition: height 0.3s, background-color 0.3s;
  }

  ::v-deep .app-header-mobile {
    height: 80px !important;
    background: linear-gradient(to bottom, #CADDFC 0px, #CADDFC 70px, rgba(202, 221, 252, 0) 80px) !important;
    z-index: 100 !important;
    transition: height 0.3s, background-color 0.3s;
  }

  ::v-deep .main-menu-mobile {
    top: 62px;
  }
}
</style>
